import { throwError } from "../../lib/throwError";
import { del, get, post, put } from "../axiosUitils";
import { Member, MemberList, MemberPayload } from "../../utils/types/member";

const basePath = "/city_collab/memberships";

class MemberService {
  /**
   * Get all members from member directory list
   */
  getAllMembers = async ({
    page,
    pageSize,
    member_status,
  }: {
    page: number;
    pageSize: number;
    member_status?: boolean;
  }): Promise<MemberList> => {
    const params: any = {
      page,
      page_size: pageSize,
    };

    if (member_status !== undefined) {
      params.member_status = member_status;
    }

    const response = await get<any>(`${basePath}/member-directory-list/`, {
      params,
    });

    // Handle the nested response structure
    if (response?.results?.data) {
      return {
        count: response.count,
        next: response.next,
        previous: response.previous,
        results: response.results.data,
      };
    }

    // Fallback for different response structures
    return {
      count: response.count ?? 0,
      next: response.next ?? null,
      previous: response.previous ?? null,
      results: response.results ?? [],
    };
  };

  /**
   * Get member by ID
   */
  getMemberById = async (id: string): Promise<Member> => {
    if (!id) {
      throwError("ID is required", "MemberService");
    }
    return await get<Member>(`${basePath}/member-directory-list/${id}/`);
  };

  /**
   * Create new member
   */
  createMember = async (payload: MemberPayload): Promise<any> => {
    if (!payload) {
      throwError("Payload is required", "MemberService");
    }

    // If profile image upload is needed, use FormData
    const useFormData = payload.member_profile_image instanceof File;

    if (useFormData) {
      const formData = new FormData();

      // Add all string fields
      Object.entries(payload).forEach(([key, value]) => {
        if (
          key !== "member_profile_image" &&
          value !== undefined &&
          value !== null
        ) {
          formData.append(key, String(value));
        }
      });

      // Add profile image if it's a File
      if (payload.member_profile_image instanceof File) {
        formData.append("member_profile_image", payload.member_profile_image);
      }

      return await post(`${basePath}/member-directory-list/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }

    return await post(`${basePath}/member-directory-list/`, payload);
  };

  /**
   * Update existing member
   */
  updateMember = async (id: string, payload: MemberPayload): Promise<any> => {
    if (!id || !payload) {
      throwError("ID and payload are required", "MemberService");
    }

    // If profile image upload is needed, use FormData
    const useFormData = payload.member_profile_image instanceof File;

    if (useFormData) {
      const formData = new FormData();

      // Add all string fields
      Object.entries(payload).forEach(([key, value]) => {
        if (
          key !== "member_profile_image" &&
          value !== undefined &&
          value !== null
        ) {
          formData.append(key, String(value));
        }
      });

      // Add profile image if it's a File
      if (payload.member_profile_image instanceof File) {
        formData.append("member_profile_image", payload.member_profile_image);
      }

      return await put(`${basePath}/member-directory-list/${id}/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }

    return await put(`${basePath}/member-directory-list/${id}/`, payload);
  };

  /**
   * Delete member
   */
  deleteMember = async (id: string): Promise<any> => {
    if (!id) {
      throwError("ID is required", "MemberService");
    }
    return await del(`${basePath}/member-directory-list/${id}/`);
  };

  /**
   * Search members by query
   */
  searchMembers = async ({
    query,
    page,
    pageSize,
  }: {
    query: string;
    page: number;
    pageSize: number;
  }): Promise<MemberList> => {
    return await get<MemberList>(`${basePath}/member-directory-list/`, {
      params: {
        search: query,
        page,
        page_size: pageSize,
      },
    });
  };

  /**
   * Get filtered member directory list
   */
  getMemberDirectoryListFilters = async (payload: {
    my_organization: boolean;
    department?: string[];
    position?: string[];
    member_status?: boolean;
  }): Promise<any> => {
    const response = await post<any>(
      `${basePath}/member-directory-list-filters/`,
      payload
    );

    // Return full response to access metadata (total_members_count, verified_members_count)
    return response;
  };
}

const instance = new MemberService();
export default instance;
